import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from 'firebase/auth'
import type { User, UserCredential, AuthError } from 'firebase/auth'
import { auth } from './config'
import { captureError as captureErrorAsync } from '../monitoring/sentry'

// Configure Google Auth Provider
const googleProvider = new GoogleAuthProvider()
googleProvider.addScope('email')
googleProvider.addScope('profile')
// Ensure we always get a fresh token
googleProvider.setCustomParameters({
  prompt: 'select_account'
})

export class AuthService {
  // Google Authentication - simplified approach following Firebase docs
  static async signInWithGoogle(): Promise<UserCredential> {
    try {
      // Starting Google sign-in process
      
      // Check if auth is available
      if (!auth) {
        throw new Error('Firebase Auth not initialized')
      }

      // Try popup first (recommended by Firebase docs)
      // Attempting Google sign-in with popup
      const result = await signInWithPopup(auth, googleProvider)
      
      // Google sign-in successful for user: ${result.user.email}
      // User details logged for debugging
      
      return result
    } catch (error) {
      // Google sign-in error occurred
      
      // If popup was blocked or failed, suggest redirect as alternative
      if ((error as AuthError).code === 'auth/popup-blocked') {
        // Popup blocked. User should try redirect method.
      }
      
      throw this.handleAuthError(error as AuthError)
    }
  }

  // Alternative redirect method for when popup fails
  static async signInWithGoogleRedirect(): Promise<void> {
    try {
      // Starting Google sign-in with redirect
      await signInWithRedirect(auth, googleProvider)
    } catch (error) {
      // Google redirect sign-in error occurred
      throw this.handleAuthError(error as AuthError)
    }
  }

  // Handle Google redirect result (for development)
  static async handleGoogleRedirectResult(): Promise<UserCredential | null> {
    try {
      const result = await getRedirectResult(auth)
      if (result) {
        // Google redirect sign-in successful
      }
      return result
    } catch (error) {
      // Google redirect result error occurred
      throw this.handleAuthError(error as AuthError)
    }
  }

  // Sign Out
  static async signOut(): Promise<void> {
    try {
      // Notify backend to blacklist the current token before signing out
      try {
        const user = auth?.currentUser
        if (user) {
          const token = await user.getIdToken()
          const apiUrl = import.meta.env.VITE_API_URL || ''
          if (apiUrl && token) {
            await fetch(`${apiUrl}/api/users/logout`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }).catch(() => {
              // Best-effort: still sign out locally even if backend call fails
            })
          }
        }
      } catch {
        // Best-effort: still sign out locally even if backend notification fails
      }

      await signOut(auth)
      // Sign out successful
    } catch (error) {
      // Sign out error occurred
      throw this.handleAuthError(error as AuthError)
    }
  }

  // Error Handling
  private static handleAuthError(error: AuthError): Error {
    const errorMessages: Record<string, string> = {
      'auth/popup-closed-by-user': 'Jendela login Google ditutup sebelum selesai. Silakan coba lagi.',
      'auth/cancelled-popup-request': 'Jendela login Google sudah terbuka. Selesaikan di jendela itu.',
      'auth/popup-blocked': 'Jendela login diblokir browser. Izinkan pop-up untuk app.tradertekno.trade, lalu coba lagi.',
      'auth/network-request-failed': 'Koneksi internet bermasalah. Periksa koneksi Anda, lalu coba lagi.',
      'auth/too-many-requests': 'Terlalu banyak percobaan. Tunggu beberapa menit, lalu coba lagi.',
      'auth/user-disabled': 'Akun ini sudah dinonaktifkan.',
      'auth/operation-not-allowed': 'Login Google sedang tidak tersedia. Hubungi support@tradertekno.com.',
      'auth/invalid-credential': 'Sesi login tidak valid atau sudah kedaluwarsa. Silakan coba lagi.',
      'auth/account-exists-with-different-credential': 'Email ini sudah terdaftar dengan cara login lain.',
      'auth/unauthorized-domain': 'Login dari alamat ini tidak diizinkan. Buka app.tradertekno.trade.',
      'auth/invalid-api-key': 'Login sedang bermasalah di sisi kami. Hubungi support@tradertekno.com.',
      'auth/app-not-authorized': 'Login sedang bermasalah di sisi kami. Hubungi support@tradertekno.com.',
      'auth/cors-unsupported': 'Browser ini tidak mendukung login Google. Coba browser lain.'
    }

    // Pesan asli Firebase berbahasa Inggris, jangan pernah diteruskan ke pengguna.
    const message = errorMessages[error.code] ?? 'Gagal masuk dengan Google. Silakan coba lagi.'
    
    // Firebase Auth Error Details logged for debugging
    // Code: ${error.code}, Message: ${error.message}
    
    // Capture error for monitoring
    captureErrorAsync(error, {
      component: 'AuthService',
      action: 'authentication',
      extra: { errorCode: error.code }
    })
    
    return new Error(message)
  }

  // Current User Helper
  static getCurrentUser(): User | null {
    return auth?.currentUser ?? null
  }

  // Auth State Observer
  static onAuthStateChanged(callback: (user: User | null) => void) {
    return onAuthStateChanged(auth, callback)
  }

  // Get user display info
  static getUserInfo(user: User) {
    return {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      emailVerified: user.emailVerified
    }
  }

  // Check if user is signed in
  static isSignedIn(): boolean {
    return !!auth?.currentUser
  }
}

export default AuthService